import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import BpmnViewer from "bpmn-js/lib/NavigatedViewer";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Portal,
} from "@mui/material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { bpmnDiagramThemeSx } from "utils/bpmnDiagramStyles";

interface PreviewAiModelProps {
  uuid: string;
  model: string;
  onApply?: (model: string) => Promise<void>;
  /**
   * `true` while the AI is still writing the response. While this is
   * set, the preview action button is replaced with a spinner and
   * disabled, so the user cannot open a preview / apply a BPMN
   * model that has not finished streaming. Allowing it caused the
   * backend to receive truncated XML and the apply step to fail
   * mid-flight.
   */
  isStreaming?: boolean;
  /**
   * Whether the current user is an administrator of the selected
   * organization. When `false`, the action button (and the "Apply"
   * button inside the preview dialog) are rendered disabled — only
   * admins can persist AI-generated models into the BPMN editor.
   * Defaults to `true` so the preview is fully usable when the prop
   * is not threaded from a parent (e.g. unit-test renders).
   */
  isAdmin?: boolean;
}

const setModel = (id: string, idParent: string, model: string) => {
  if (!model || model === "undefined" || !model.trim()) {
    return;
  }
  const canvas = document.getElementById(id) as HTMLCanvasElement;
  const parent = document.getElementById(idParent) as HTMLElement;
  if (!canvas || !parent) return;
  canvas.innerHTML = "";

  const bpmnViewer = new BpmnViewer({
    container: canvas,
    readOnly: true,
    selectable: false,
    additionalModules: [],
  });
  (async () => {
    try {
      await bpmnViewer.importXML(model);
      const _canvas = bpmnViewer.get("canvas") as any;
      const viewbox = _canvas.viewbox();

      if (viewbox) {
        parent.style.height = viewbox.height;
        canvas.style.height = viewbox.height;
        _canvas.zoom("fit-viewport");
      }
    } catch (e) {
      console.warn("BPMN XML parsing error (this is expected during streaming):", e);
    }
  })();
};

const PreviewAiModel: React.FC<PreviewAiModelProps> = ({
  model,
  onApply,
  uuid,
  isStreaming = false,
  isAdmin = true,
}) => {
  const parentId = useMemo(() => `container-ai-model-${uuid}`, [uuid]);
  const idElement = useMemo(() => `ai-${uuid}`, [uuid]);
  const translations = useTranslations();
  const parentIdElementPreview = useMemo(
    () => `preview-container-ai-model-${uuid}`,
    [uuid]
  );
  const idElementPreview = useMemo(() => `preview-ai-${uuid}`, [uuid]);

  const [openPreview, setOpenPreview] = useState<boolean>(false);

  // Defensive: if `onClick` somehow fires after our `onMouseDown`
  // (the normal browser sequence), we must NOT re-open the dialog
  // — opening it twice is a no-op but causes the dialog to flash
  // closed-and-re-opened if the first open hadn't committed yet. A
  // ref (not state) is enough: this flag is internal to the click
  // event lifetime, not something we want to trigger a re-render.
  const armedFromMouseDownRef = useRef(false);

  useEffect(() => {
    setModel(idElement, parentId, model);
  }, [idElement, model, parentId]);

  // Bound to the "Applica" button inside the preview dialog — see
  // the equivalent `handlePreview*` handlers for the rationale.
  const handleApply = () => {
    onApply?.(model);
    setOpenPreview(false);
  };

  const handleApplyMouseDown = () => {
    armedFromMouseDownRef.current = true;
    handleApply();
  };

  const handleApplyClick = () => {
    if (armedFromMouseDownRef.current) {
      armedFromMouseDownRef.current = false;
      return;
    }
    handleApply();
  };

  const handlePreview = () => {
    setOpenPreview(true);
  };

  // Bound to the "Azioni" button's `mousedown` — see the long comment
  // on the JSX below for the rationale.
  const previewMouseDownTimerRef = useRef<number | null>(null);
  const handlePreviewMouseDown = () => {
    // Set the flag BEFORE the React state update so the eventual
    // (possibly swallowed) `click` event sees it.
    armedFromMouseDownRef.current = true;
    // Trigger immediately on mousedown. If the browser later fires
    // a `click` for the same interaction, the `onClick` handler
    // will check the flag and bail out (and reset it for the next
    // interaction).
    handlePreview();
    // Safety net: if the matching `click` is swallowed (the case
    // we are working around), the flag would otherwise stay `true`
    // forever and silently break the next click. Clear it after a
    // generous timeout matching the browser's normal mousedown →
    // mouseup → click sequence.
    if (previewMouseDownTimerRef.current !== null) {
      window.clearTimeout(previewMouseDownTimerRef.current);
    }
    previewMouseDownTimerRef.current = window.setTimeout(() => {
      armedFromMouseDownRef.current = false;
      previewMouseDownTimerRef.current = null;
    }, 500);
  };

  // Cleanup the safety-net timer on unmount.
  useEffect(
    () => () => {
      if (previewMouseDownTimerRef.current !== null) {
        window.clearTimeout(previewMouseDownTimerRef.current);
      }
    },
    []
  );

  const handlePreviewClick = () => {
    if (armedFromMouseDownRef.current) {
      armedFromMouseDownRef.current = false;
      return;
    }
    handlePreview();
  };

  useLayoutEffect(() => {
    if (openPreview) {
      // console.log(openPreview, "fuori timeout")
      setTimeout(() => {
        setModel(idElementPreview, parentIdElementPreview, model,);
        // console.log(openPreview, "dentro timeout")
      });
      // console.log(openPreview, "sotto tiemout")
    }
  }, [idElementPreview, model, openPreview, parentIdElementPreview]);

  return (
    <React.Fragment>
      {/* The `minHeight` guarantees the absolute-positioned overlay
          (which holds the "Azioni" button) covers a clickable area
          from the very first render — `setModel` (in the effect above)
          only sets the height asynchronously after `bpmnViewer` has
          parsed the XML and computed the viewbox. Without this, the
          overlay collapses to 0px and the first user click is captured
          by the viewer DOM before the layout stabilises. */}
      <Box sx={{ position: "relative", minHeight: 200 }} id={parentId}>
        {/*
          The overlay wraps the "Azioni" button. The button itself
          is wired to trigger the action on `mousedown` (defensively,
          in case the matching `click` is swallowed by an ancestor's
          capture-phase listener) AND on `click` (the normal path).
        */}
        <Box sx={{ position: "absolute", inset: 0, zIndex: 10 }}>
          <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
            {isStreaming ? (
              // While the AI is still streaming the model XML, the
              // "apply / preview" action must be unavailable. The
              // bpmn-js preview canvas only renders whatever XML it
              // has so far, so opening it (or applying it) before
              // the response has finished would push a truncated /
              // invalid model to the backend. We render a disabled
              // button with a spinner + "Attendere…" caption so the
              // user has a clear visual signal that the action will
              // become available as soon as the AI finishes.
              (() => {
                const waitingLabel =
                  getTranslation(
                    translations,
                    "ai_assistant.action.waiting"
                  ) || "Attendere…";
                return (
                  <FlowentiButton
                    variant="contained"
                    size="small"
                    disabled
                    startIcon={
                      <CircularProgress
                        size={14}
                        thickness={5}
                        sx={{ color: "inherit" }}
                      />
                    }
                    title={waitingLabel}
                    sx={{ position: "absolute", bottom: 2, right: 2 }}
                  >
                    {waitingLabel}
                  </FlowentiButton>
                );
              })()
            ) : (
              <FlowentiButton
                // We bind the action to BOTH `mousedown` and `click`
                // instead of just `click`. Reason: in some parent
                // configurations (e.g. the AiDrawer's `Drawer` focus
                // trap, or diagram-js' `Dragging` service binding
                // capture-phase `mousedown`/`mouseup` listeners on
                // `document` to swallow click-through), the first
                // `mousedown` lands on the button but the matching
                // `mouseup` / `click` is preventDefault'd by an
                // ancestor — the browser then fails to synthesise
                // the `click`, and the user has to click a SECOND
                // time for the action to actually fire.
                //
                // Firing on `mousedown` is the bulletproof fix: the
                // button has already received the event and the
                // action runs immediately. The companion flag in
                // `armedFromMouseDownRef` (reset by `handlePreview`)
                // guards `onClick` against the double-trigger in the
                // normal case where the browser DOES fire a follow-up
                // `click`.
                onMouseDown={handlePreviewMouseDown}
                onClick={handlePreviewClick}
                variant="contained"
                size="small"
                // The action is admin-only: only administrators of the
                // current organization can persist AI-generated models.
                // The button is rendered with `disabled` so MUI styles
                // it as inactive (no hover, cursor not-allowed).
                disabled={!isAdmin}
                sx={{ position: "absolute", bottom: 2, right: 2 }}
              >
                {getTranslation(translations, "ai_assistant.action")}
              </FlowentiButton>
            )}
          </Box>
        </Box>
        <Box
          id={idElement}
          sx={{
            ...bpmnDiagramThemeSx,
            "& .bjs-drilldown": { display: "none" },
            "& .djs-minimap": { display: "none" },
            "& .djs-palette": { display: "none" },
            width: 250,
            // Block click capture by the bpmn-js viewer. The viewer
            // injects canvas/SVG children with `pointer-events: auto`
            // by default, and the inline `none` on the parent does NOT
            // propagate to dynamically-injected descendants — so the
            // first click before the viewer's layout settles could
            // land on the canvas instead of the "Azioni" overlay.
            // The `& *` selector covers every descendant.
            pointerEvents: "none",
            "& *": { pointerEvents: "none" },
          }}
          className="canvas"
        />
      </Box>
      <Portal container={document.body}>
        <Dialog
          sx={{ zIndex: 99999999 }}
          onClose={() => setOpenPreview(false)}
          open={openPreview}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "#000000" : "background.paper",
              color: "text.primary",
            },
          }}
        >
          <DialogContent
            id={parentIdElementPreview}
            sx={{
              height: "75vh",
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "#000000" : "background.paper",
              p: 0,
            }}
          >
            <Box
              id={idElementPreview}
              sx={{
                ...bpmnDiagramThemeSx,
                "& .bjs-drilldown": { display: "none" },
                "& .djs-minimap": { display: "none" },
                "& .djs-palette": { display: "none" },
                width: "100%",
                height: "100%",
                // Same rationale as the inline preview: block the
                // bpmn-js viewer's children from intercepting clicks
                // (drag-to-pan, etc.) inside the modal dialog.
                pointerEvents: "none",
                "& *": { pointerEvents: "none" },
              }}
              className="canvas"
            />
          </DialogContent>
          {onApply && (
            <DialogActions
              sx={{
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "#000000" : "background.paper",
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <FlowentiButton
                type="button"
                variant="contained"
                onClick={handleApplyClick}
                onMouseDown={handleApplyMouseDown}
                // Admin-only apply, matching the inline button.
                disabled={!isAdmin}
              >
                {getTranslation(translations, "apply_ai_edit.to.model")}
              </FlowentiButton>
            </DialogActions>
          )}
        </Dialog>
      </Portal>
    </React.Fragment>
  );
};

export default PreviewAiModel;
